import CopyAllOutlined from "@mui/icons-material/CopyAllOutlined";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import ScoutIcon from "components/ScoutIcon";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { useCallback, useMemo } from "react";

import {
  ScoutChatMessage,
  ScoutMessagePart,
  ScoutTextPart,
  ScoutToolPart,
} from "../hooks/useScoutChatWithMocks";
import { ToolCallGroup } from "./ToolCallGroup";

type RenderBlock =
  | { kind: "text"; id: string; part: ScoutTextPart }
  | { kind: "tool-group"; id: string; tools: ScoutToolPart[] };

function groupParts(parts: ScoutMessagePart[]): RenderBlock[] {
  const blocks: RenderBlock[] = [];
  for (const part of parts) {
    if (part.type === "tool") {
      const last = blocks[blocks.length - 1];
      if (last?.kind === "tool-group") {
        last.tools.push(part);
      } else {
        blocks.push({ kind: "tool-group", id: part.id, tools: [part] });
      }
    } else if (part.type === "text") {
      blocks.push({ kind: "text", id: part.id, part });
    }
  }
  return blocks;
}

export function AgentMessage({ message }: { message: ScoutChatMessage }) {
  const blocks = useMemo<RenderBlock[]>(() => {
    if (message.parts && message.parts.length > 0) return groupParts(message.parts);
    return [
      {
        kind: "text",
        id: `${message.id}-legacy`,
        part: { id: `${message.id}-legacy`, type: "text", text: message.content ?? "" },
      },
    ];
  }, [message.parts, message.content, message.id]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        py: 1.5,
        px: { xs: 2, sm: 3 },
      }}
    >
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={1}
        sx={{
          maxWidth: "90%",
          px: 2,
          py: 1.25,
          borderRadius: 3,
          bgcolor: "transparent",
          color: "text.primary",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        <Box
          sx={{
            "@keyframes scoutSpin": {
              from: { transform: "rotate(0deg)" },
              to: { transform: "rotate(360deg)" },
            },
          }}
        >
          <ScoutIcon
            sx={{
              color: "#B0BEC5",
              animation: message.isStreaming ? "scoutSpin 1.1s linear infinite" : "none",
              transformOrigin: "center",
            }}
          />
        </Box>

        <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
          {blocks.map((block) =>
            block.kind === "tool-group" ? (
              <ToolCallGroup key={block.id} toolParts={block.tools} />
            ) : (
              <Typography key={block.id} variant="body1" component="div">
                {block.part.text}
              </Typography>
            )
          )}
          <AgentMessageToolbar message={message} />
        </Stack>
      </Stack>
    </Box>
  );
}

function AgentMessageToolbar({ message }: { message: ScoutChatMessage }) {
  const copyToClipboard = useCopyToClipboard();

  const handleCopy = useCallback(async () => {
    const text = message.content ?? "";
    await copyToClipboard(text);
  }, [copyToClipboard, message.content]);

  return (
    <Stack width="100%" direction="row" alignItems="center" spacing={1}>
      <Tooltip title="Copy text">
        <IconButton aria-label="Copy text" size="small" onClick={handleCopy}>
          <CopyAllOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
